import type { FormEvent, ReactElement } from 'react'
import { useEffect, useId, useState } from 'react'
import type { UnitPriceRow } from '@/domain/units'
import { formatMinor } from '~/api/client'
import { chartCss } from '~/charts/theme'
import { Button } from '~/components/ui/Controls'
import { Field, Modal } from '~/components/ui/Modal'
import { today } from '~/state/dates'
import type { UnitDraft } from '~/state/units'
import {
  emptyUnitDraft,
  refuseUnit,
  toDefineUnit,
  toUnitPrice,
  useDefineUnit,
  useRecordUnitPrice,
  useRemoveUnit,
  useRenameUnit,
  useRemoveUnitPrice,
  useUnitPrices,
} from '~/state/units'

export const UNIT_CSS = `
.nw-unit-dialog p { color: var(--nw-body-ink); font-size: var(--nw-text-cell); line-height: 1.55; margin: 0 0 var(--nw-space-3); max-width: 62ch; }
.nw-unit-dialog h3 { color: var(--nw-faint); font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong); letter-spacing: var(--nw-track-label); margin: var(--nw-space-4) 0 var(--nw-space-3); text-transform: uppercase; }
.nw-unit-dialog .nw-unit-row { display: grid; gap: var(--nw-space-3); grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); margin-bottom: var(--nw-space-3); }
.nw-unit-dialog .nw-error { color: var(--nw-critical); font-size: var(--nw-text-cell); margin: 0 0 var(--nw-space-3); }
.nw-unit-dialog .nw-said { background: var(--nw-hover); border-radius: var(--nw-radius-md); color: var(--nw-body-ink); font-size: var(--nw-text-cell); margin: 0 0 var(--nw-space-3); padding: 10px var(--nw-space-3); }
.nw-unit-dialog .nw-observed { list-style: none; margin: 0 0 var(--nw-space-3); max-height: 210px; overflow: auto; padding: 0; }
.nw-unit-dialog .nw-observed li { align-items: center; border-top: var(--nw-hairline-width) solid var(--nw-row-rule); display: flex; gap: var(--nw-space-3); justify-content: space-between; padding: var(--nw-space-2) 0; }
.nw-unit-dialog .nw-observed small { color: var(--nw-muted); }
.nw-unit-dialog .nw-unit-actions { align-items: center; border-top: var(--nw-hairline-width) solid var(--nw-hairline); display: flex; flex-wrap: wrap; gap: var(--nw-space-2); justify-content: flex-end; margin-top: var(--nw-space-2); padding-top: var(--nw-space-4); }
.nw-unit-dialog .nw-unit-remove { align-items: center; display: flex; flex-wrap: wrap; gap: var(--nw-space-2); margin-right: auto; }
.nw-unit-dialog .nw-confirm { color: var(--nw-critical); font-size: var(--nw-text-cell); }
.nw-unit-dialog .nw-unit-name { align-items: center; display: flex; gap: var(--nw-space-3); justify-content: space-between; }
.nw-unit-dialog .nw-unit-rename-actions { align-items: end; display: flex; gap: var(--nw-space-2); }
`

export interface DialogUnit {
  key: string
  label: string
  currency: string
  observations: number
}

/**
 * Where a household chooses what it measures itself against, and what that
 * thing has cost.
 *
 * Nothing is shipped with the product. An espresso in Aarhus means something to
 * exactly one reader, and a yardstick the reader did not choose is not a
 * yardstick — so the only prices this page ever draws are the ones entered
 * here.
 */
export function UnitDialog({
  unit,
  baseCurrency,
  onClose,
}: {
  /** The unit being priced, absent while one is being defined. */
  unit?: DialogUnit
  baseCurrency: string
  onClose: () => void
}): ReactElement {
  useEffect(() => {
    const opener = document.activeElement
    // Whatever opened the dialog gets the focus back. The element only does that
    // for itself when it is the one doing the closing, and this one is closed by
    // being taken off the page.
    return () => {
      if (opener instanceof HTMLElement) opener.focus()
    }
  }, [])

  const naming = unit === undefined
  const [draft, setDraft] = useState<UnitDraft>(() =>
    emptyUnitDraft(unit?.currency ?? baseCurrency, today()),
  )
  const [refusal, setRefusal] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const field = useId()

  const history = useUnitPrices(unit?.key ?? null)
  const define = useDefineUnit()
  const record = useRecordUnitPrice()
  const removePrice = useRemoveUnitPrice()
  const removeUnit = useRemoveUnit()
  const rename = useRenameUnit()

  const change = (patch: Partial<UnitDraft>): void => setDraft({ ...draft, ...patch })
  const [newName, setNewName] = useState<string | null>(null)
  const pending = define.isPending || record.isPending
  const failure =
    define.error ??
    record.error ??
    rename.error ??
    removePrice.error ??
    removeUnit.error ??
    history.error

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    const refused = refuseUnit(draft, naming)
    if (refused !== undefined) {
      setRefusal(refused)
      return
    }
    setRefusal(null)

    if (unit === undefined) {
      define.mutate(toDefineUnit(draft), { onSuccess: onClose })
      return
    }
    record.mutate(
      { key: unit.key, price: toUnitPrice(draft) },
      // Left open on purpose: a unit worth pricing twice is worth pricing five
      // times, and closing after each one would make the history the page asks
      // for five trips through the same dialog.
      { onSuccess: () => setDraft({ ...draft, priceMajor: '', source: '' }) },
    )
  }

  const prices = history.data?.prices ?? []
  const observations = history.data?.unit.observations ?? unit?.observations ?? 0
  const name = history.data?.unit.label ?? unit?.label ?? ''

  return (
    <div className="nw-unit-dialog">
      <Modal
        title={naming ? 'Define a unit' : name}
        lede={
          naming
            ? 'Whatever you would rather read your wealth in than kroner.'
            : 'What one has cost, and when.'
        }
        labelledBy="nw-unit-title"
        onClose={onClose}
      >
        <style>{chartCss + UNIT_CSS}</style>
        <form onSubmit={submit} aria-label={naming ? 'Define a unit' : `Price ${name}`}>
          {naming ? (
            <p>
              An espresso, a square metre of a flat, a gram of gold. The page divides your net worth
              by what one costs, so the count moves for two reasons — the wealth and the price — and
              shows them apart.
            </p>
          ) : null}

          {observations === 1 && (
            <p className="nw-said">
              One price is not a history. Until a second date is entered, the line divides a
              changing net worth by an unchanging figure and draws your wealth back at you rather
              than what it buys.
            </p>
          )}

          {naming && (
            <div className="nw-unit-row">
              <Field id={`${field}-label`} label="Name">
                <input
                  id={`${field}-label`}
                  value={draft.label}
                  placeholder="Espresso at an Aarhus café"
                  onChange={(event) => change({ label: event.target.value })}
                />
              </Field>
              <Field id={`${field}-currency`} label="Currency" width="narrow">
                <input
                  id={`${field}-currency`}
                  value={draft.currency}
                  maxLength={3}
                  onChange={(event) => change({ currency: event.target.value.toUpperCase() })}
                />
              </Field>
            </div>
          )}

          <div className="nw-unit-row">
            <Field id={`${field}-price`} label={`What one costs, in ${draft.currency || baseCurrency}`}>
              <input
                id={`${field}-price`}
                inputMode="decimal"
                value={draft.priceMajor}
                placeholder="38"
                onChange={(event) => change({ priceMajor: event.target.value })}
              />
            </Field>
            <Field id={`${field}-date`} label="On">
              <input
                id={`${field}-date`}
                type="date"
                value={draft.asOf}
                onChange={(event) => change({ asOf: event.target.value })}
              />
            </Field>
          </div>

          <Field id={`${field}-source`} label="Where the price came from" width="set">
            <input
              id={`${field}-source`}
              value={draft.source}
              placeholder="the menu"
              onChange={(event) => change({ source: event.target.value })}
            />
          </Field>

          {refusal !== null && <p className="nw-error">{refusal}</p>}
          {failure && <p className="nw-error">{failure.message}</p>}

          {/* A unit is a description — "one gram of gold", "an espresso at the
              usual cafe" — and descriptions get refined. Renaming leaves the
              key alone, so the prices below stay where they are; the only other
              way to fix a name used to be removing the unit and losing them. */}
          {!naming && (
            <>
              <h3>Name</h3>
              {newName === null ? (
                <p className="nw-unit-name">
                  <span>{name}</span>
                  <Button onClick={() => setNewName(name)}>Rename</Button>
                </p>
              ) : (
                <div className="nw-unit-row">
                  <Field id={`${field}-rename`} label="What to call it instead">
                    <input
                      id={`${field}-rename`}
                      value={newName}
                      onChange={(event) => setNewName(event.target.value)}
                    />
                  </Field>
                  <span className="nw-unit-rename-actions">
                    <Button
                      kind="primary"
                      disabled={rename.isPending || newName.trim() === ''}
                      onClick={() =>
                        rename.mutate(
                          { key: unit.key, label: newName.trim() },
                          { onSuccess: () => setNewName(null) },
                        )
                      }
                    >
                      Rename it
                    </Button>
                    <Button onClick={() => setNewName(null)}>Cancel</Button>
                  </span>
                </div>
              )}

              <h3>Prices on file</h3>
              {history.isPending ? (
                <p>Loading…</p>
              ) : (
                <ul className="nw-observed">
                  {prices.map((price: UnitPriceRow) => (
                    <li key={price.asOf}>
                      <span>
                        {price.asOf} · {formatMinor(price.priceMinor, price.currency)}{' '}
                        <small>({price.source})</small>
                      </span>
                      <Button
                        disabled={prices.length < 2 || removePrice.isPending}
                        onClick={() => removePrice.mutate({ key: unit.key, asOf: price.asOf })}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          <div className="nw-unit-actions">
            {!naming && (
              <span className="nw-unit-remove">
                {confirming ? (
                  <>
                    <span className="nw-confirm">
                      Remove {name} and every price on it?
                    </span>
                    <Button
                      kind="danger"
                      disabled={removeUnit.isPending}
                      onClick={() => removeUnit.mutate(unit.key, { onSuccess: onClose })}
                    >
                      Remove
                    </Button>
                    <Button onClick={() => setConfirming(false)}>Keep</Button>
                  </>
                ) : (
                  <Button kind="danger" onClick={() => setConfirming(true)}>
                    Remove the unit
                  </Button>
                )}
              </span>
            )}
            <Button onClick={onClose}>{naming ? 'Cancel' : 'Done'}</Button>
            <button type="submit" className="nw-btn" data-kind="primary" disabled={pending}>
              {naming ? 'Define it' : 'Add this price'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
